import { useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { sourceIcon, fmtDate } from "../lib/constants";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, Send, MessageSquare, AlertTriangle } from "lucide-react";

export default function SmsCenter() {
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [configured, setConfigured] = useState(true);

  const { data: leads = [] } = useQuery({
    queryKey: ["leads"],
    queryFn: () => api.get("/leads").then((r) => r.data),
  });

  useEffect(() => {
    api.get("/sms/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/sms/config").then((r) => setConfigured(r.data.configured)).catch(() => {});
  }, []);

  const loadThread = useCallback(async (lead) => {
    setSelected(lead);
    const { data } = await api.get(`/leads/${lead.id}/messages`);
    setMessages(data);
  }, []);

  const send = async () => {
    if (!text.trim() || !selected) return;
    try {
      await api.post("/sms/send", { lead_id: selected.id, body: text });
      setText("");
      toast.success("SMS sent");
      loadThread(selected);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const simulateReply = async () => {
    if (!selected) return;
    try {
      await api.post("/dev/simulate-reply", { lead_id: selected.id, body: "Yes, I'm interested — please call me!" });
      toast.success("Lead reply simulated");
      loadThread(selected);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const callable = leads.filter((l) => l.phone && (!q || l.name.toLowerCase().includes(q.toLowerCase())));

  return (
    <div data-testid="sms-center-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">SMS Center</h1>
        <p className="text-muted-foreground mt-1">Text leads, send install links, and keep the conversation going.</p>
      </div>

      {!configured && (
        <div className="flex items-center gap-2 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 mb-5">
          <AlertTriangle className="h-4 w-4" />
          Twilio SMS is not configured yet. Add your Twilio credentials to send real messages.
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 h-[65vh]">
        <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col">
          <div className="p-4 border-b border-border relative">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find contact..." className="pl-9" data-testid="sms-search" />
          </div>
          <div className="overflow-y-auto flex-1">
            {callable.map((lead) => {
              const Icon = sourceIcon(lead.source);
              return (
                <button
                  key={lead.id}
                  onClick={() => loadThread(lead)}
                  className={`w-full text-left px-4 py-3 border-b border-border flex items-center gap-3 hover:bg-secondary/40 ${selected?.id === lead.id ? "bg-primary/10" : ""}`}
                  data-testid={`sms-contact-${lead.id}`}
                >
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{lead.name}</p>
                    <p className="text-xs font-mono text-muted-foreground">{lead.phone}</p>
                  </div>
                </button>
              );
            })}
            {callable.length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No contacts with phone numbers.</p>}
          </div>
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-border bg-card flex flex-col">
          {selected ? (
            <>
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div>
                  <p className="font-semibold">{selected.name}</p>
                  <p className="text-xs font-mono text-muted-foreground">{selected.phone}</p>
                </div>
                <Button size="sm" variant="outline" onClick={simulateReply} data-testid="simulate-reply-button">
                  Simulate reply
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="sms-thread-view">
                {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No messages yet. Say hello!</p>}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "inbound" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2 text-sm ${m.direction === "inbound" ? "bg-secondary" : "bg-primary text-primary-foreground"}`}>
                      {m.body}
                      <div className={`text-[10px] mt-1 ${m.direction === "inbound" ? "text-muted-foreground" : "text-primary-foreground/70"}`}>
                        {fmtDate(m.created_at)} {m.meta?.status ? `· ${m.meta.status}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-border space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {templates.map((t) => (
                    <button key={t.id} onClick={() => setText(t.body)} className="text-xs px-2 py-1 rounded-md bg-secondary hover:bg-secondary/70 text-muted-foreground" data-testid={`sms-template-${t.id}`}>
                      {t.title}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message ({name} = lead name)" data-testid="sms-compose-input" />
                  <Button onClick={send} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="sms-send-button">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessageSquare className="h-10 w-10 mb-3 text-primary" />
              <p>Select a contact to start texting.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
